import * as React from "react";
import { ArrowLeft, Check, ChevronRight, Clock, Plus, X } from "lucide-react";

import { AsyncButton } from "../ui/async-button.js";
import { Button } from "../ui/button.js";
import { DateInput } from "../ui/date-input.js";
import { Input } from "../ui/input.js";
import { Textarea } from "../ui/textarea.js";
import { TimeInput } from "../ui/time-input.js";
import { cn } from "../../lib/utils.js";
import {
  CATEGORY_LABEL, CATEGORY_ORDER, TODO_LIMITS, categoryOf, normalizeTags, splitTagInput, todayKey, validateDraft,
  type CalendarTodo, type TodoCategory, type TodoDraft,
} from "../../lib/calendarTodos.js";
import { CATEGORY_STYLE, formatDayTitle, formatTime } from "./todoStyles.js";

export type CardView = { kind: "list" } | { kind: "detail"; id: string } | { kind: "form"; id?: string };

interface Props {
  dateKey: string;
  isToday: boolean;
  isPast: boolean;
  /** The day's todos after category filters, already ordered. */
  todos: CalendarTodo[];
  hiddenByFilters: number;
  lookup: (id: string) => CalendarTodo | undefined;
  initialView: CardView;
  /** Mobile: renders as a bottom sheet with a drag handle. */
  sheet: boolean;
  /** Runtime-measured placement; see the custom-property note below. */
  placement?: { left: number; top: number; side: "left" | "right" | "center"; arrowY: number } | undefined;
  onClose: () => void;
  onShowAll: () => void;
  onToggle: (id: string, done: boolean) => void;
  onSave: (draft: TodoDraft, id?: string) => Promise<CalendarTodo | null>;
  onDelete: (id: string) => Promise<void>;
  onMoveOverdue: () => void;
}

const iconBtn = "flex size-[30px] shrink-0 items-center justify-center rounded-[9px] text-surface-500 transition-colors hover:bg-surface-100 hover:text-surface-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500";
const sectionLabel = "mb-1.5 mt-4 text-[11px] font-bold uppercase tracking-[0.1em] text-surface-500";
const fieldLabel = "mb-1 text-[11px] font-bold uppercase tracking-[0.08em] text-surface-500";

function RoundCheck({ done, category, onToggle, label }: { done: boolean; category: TodoCategory; onToggle: () => void; label: string }) {
  const s = CATEGORY_STYLE[category];
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={done}
      aria-label={label}
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      className={cn(
        "flex size-[22px] shrink-0 items-center justify-center rounded-full border-2 p-0 transition-[background,transform] duration-200 active:scale-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500",
        s.check, done && s.checkOn,
      )}
    >
      <Check className={cn("size-3.5 transition-all duration-200", done ? "scale-100 opacity-100" : "scale-50 opacity-0")} strokeWidth={3} />
    </button>
  );
}

function CategoryChip({ category }: { category: TodoCategory }) {
  return (
    <span className={cn("inline-flex h-6 items-center gap-1.5 rounded-full px-2.5 text-[11.5px] font-semibold", CATEGORY_STYLE[category].chip)}>
      <i className={cn("size-[7px] rounded-full", CATEGORY_STYLE[category].dot)} />
      {CATEGORY_LABEL[category]}
    </span>
  );
}

export function TodoDayCard(props: Props) {
  const { dateKey, isToday, isPast, todos, hiddenByFilters, lookup, sheet, placement, onClose } = props;
  const [view, setView] = React.useState<CardView>(props.initialView);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const headingId = React.useId();
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [dragY, setDragY] = React.useState(0);
  const dragStart = React.useRef<number | null>(null);

  // Opening (or switching day) resets the card and moves focus into it.
  React.useEffect(() => { setView(props.initialView); setConfirmDelete(false); }, [dateKey, props.initialView]);
  React.useEffect(() => { rootRef.current?.focus(); }, [dateKey]);
  React.useEffect(() => {
    if (!confirmDelete) return;
    const t = setTimeout(() => setConfirmDelete(false), 4000);
    return () => clearTimeout(t);
  }, [confirmDelete]);

  const openCount = todos.filter(t => !t.done).length;
  const doneCount = todos.length - openCount;
  const detail = view.kind === "detail" ? lookup(view.id) : undefined;

  // The form owns an unsaved-changes guard; it registers it here so Esc can ask first.
  const guardRef = React.useRef<(() => boolean) | null>(null);
  function requestClose() { if (guardRef.current?.()) return; onClose(); }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") { e.stopPropagation(); requestClose(); }
  }

  // The card's left/top/arrow come from measured cell rects every resize, so
  // they cannot be static classes -- custom properties only, consumed by classes.
  const placementVars = placement && !sheet
    ? ({ "--td-left": `${placement.left}px`, "--td-top": `${placement.top}px`, "--td-arrow-y": `${placement.arrowY}px` } as React.CSSProperties)
    : undefined;
  const sheetVars = sheet && dragY ? ({ "--td-drag": `${dragY}px` } as React.CSSProperties) : undefined;

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-modal="false"
      aria-labelledby={headingId}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      data-testid="todo-day-card"
      style={placementVars ?? sheetVars}
      className={cn(
        "z-40 flex flex-col bg-surface-0 shadow-2xl ring-1 ring-inset ring-surface-200 outline-none",
        sheet
          ? "fixed inset-x-0 bottom-0 h-[80vh] translate-y-[var(--td-drag,0px)] rounded-t-[18px]"
          : "absolute left-[var(--td-left)] top-[var(--td-top)] h-[420px] w-[340px] rounded-[18px] animate-td-pop",
      )}
    >
      {!sheet && placement && placement.side !== "center" && (
        <span
          aria-hidden="true"
          className={cn(
            "absolute top-[var(--td-arrow-y)] size-3 rotate-45 bg-surface-0",
            placement.side === "left" ? "-left-1.5 ring-1 ring-surface-200 [clip-path:polygon(0_100%,0_0,100%_0)]" : "-right-1.5 ring-1 ring-surface-200 [clip-path:polygon(100%_0,100%_100%,0_100%)]",
          )}
        />
      )}
      {sheet && (
        <div
          className="flex h-6 shrink-0 cursor-grab touch-none items-center justify-center"
          onTouchStart={(e) => { dragStart.current = e.touches[0]!.clientY; }}
          onTouchMove={(e) => { if (dragStart.current != null) setDragY(Math.max(0, e.touches[0]!.clientY - dragStart.current)); }}
          onTouchEnd={() => { if (dragY > 100) requestClose(); setDragY(0); dragStart.current = null; }}
        >
          <span className="h-1 w-10 rounded-full bg-surface-300" />
        </div>
      )}

      {view.kind === "list" && (
        <>
          <div className="flex shrink-0 items-start justify-between pb-2 pl-[18px] pr-3 pt-4">
            <div>
              <h2 id={headingId} className="flex items-center gap-2 text-[17px] font-bold tracking-tight">
                {formatDayTitle(dateKey)}
                {isToday && <span className="rounded-full bg-brand-100 px-2 py-0.5 text-[11px] font-bold text-brand-700">Today</span>}
              </h2>
              <p className="mt-0.5 text-[12.5px] text-surface-500">
                {todos.length === 0 ? "Nothing planned" : `${openCount} open · ${doneCount} done`}
                {hiddenByFilters > 0 && (
                  <> · {hiddenByFilters} hidden by filters{" "}
                    <button type="button" className="font-semibold text-brand-600 underline" onClick={props.onShowAll}>Show all</button>
                  </>
                )}
              </p>
            </div>
            <button type="button" aria-label="Close" className={iconBtn} onClick={requestClose}><X size={16} /></button>
          </div>

          {isPast && openCount > 0 && (
            <div className="mx-4 mb-1.5 flex shrink-0 items-center justify-between gap-2.5 rounded-[11px] bg-[var(--color-error-bg)] px-3 py-2 text-[12.5px] text-[color:var(--color-error-fg)] ring-1 ring-inset ring-[color:var(--color-error-border)]">
              <span>{openCount} unfinished from this day</span>
              <Button size="sm" variant="outline" onClick={props.onMoveOverdue}>Move to today</Button>
            </div>
          )}

          <div className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-2.5 pb-1.5 pt-1">
            {todos.length === 0 && <p className="px-3.5 py-8 text-center text-[13px] text-surface-500">No todos for this day yet.</p>}
            {todos.map((t) => {
              const cat = categoryOf(t.tone);
              return (
                <div
                  key={t.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setView({ kind: "detail", id: t.id })}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setView({ kind: "detail", id: t.id }); } }}
                  className="flex animate-td-row cursor-pointer items-center gap-[11px] rounded-xl px-2 py-[9px] transition-colors hover:bg-surface-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500"
                >
                  <RoundCheck done={t.done} category={cat} label={`${t.done ? "Mark open" : "Mark done"}: ${t.title}`} onToggle={() => props.onToggle(t.id, !t.done)} />
                  <div className="min-w-0 flex-1">
                    <div className={cn("truncate text-sm font-medium", t.done && "text-surface-400 line-through")}>{t.title}</div>
                    <div className={cn("mt-0.5 flex items-center gap-1.5 overflow-hidden whitespace-nowrap text-[11.5px] text-surface-500", t.done && "opacity-60")}>
                      <i className={cn("size-1.5 shrink-0 rounded-full", CATEGORY_STYLE[cat].dot)} />
                      <span>{CATEGORY_LABEL[cat]}</span>
                      {t.time && <span>· {formatTime(t.time)}</span>}
                      {t.tags.slice(0, 2).map(tag => <span key={tag}>#{tag}</span>)}
                      {t.tags.length > 2 && <span>+{t.tags.length - 2}</span>}
                    </div>
                  </div>
                  <ChevronRight size={16} className="shrink-0 text-surface-400" />
                </div>
              );
            })}
          </div>

          <div className="shrink-0 border-t border-surface-200 px-3.5 pb-3.5 pt-2.5">
            <Button className="w-full" disabled={isPast} title={isPast ? "Todos can only be added from today onwards" : undefined} onClick={() => setView({ kind: "form" })}><Plus size={15} /> Add todo</Button>
          </div>
        </>
      )}

      {view.kind === "detail" && (
        detail ? (
          <>
            <div className="flex shrink-0 items-center justify-between px-3 pb-1 pt-3">
              <button type="button" aria-label="Back to list" className={iconBtn} onClick={() => setView({ kind: "list" })}><ArrowLeft size={16} /></button>
              <span className="text-[12.5px] font-semibold text-surface-500">{formatDayTitle(dateKey)}</span>
              <button type="button" aria-label="Close" className={iconBtn} onClick={requestClose}><X size={16} /></button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-[18px] pb-2.5 pt-0.5">
              <h2 id={headingId} className="line-clamp-3 text-[19px] font-bold leading-[25px] tracking-tight">{detail.title}</h2>
              <div className="mt-2.5 flex flex-wrap gap-1.5">
                <span className={cn("inline-flex h-6 items-center gap-1.5 rounded-full px-2.5 text-[11.5px] font-bold", detail.done ? "bg-[var(--color-success-bg)] text-[color:var(--color-success-fg)]" : "bg-[var(--color-info-bg)] text-[color:var(--color-info-fg)]")}>
                  {detail.done && <Check size={12} strokeWidth={3} />}{detail.done ? "Done" : "Open"}
                </span>
                <CategoryChip category={categoryOf(detail.tone)} />
                {detail.time && (
                  <span className="inline-flex h-6 items-center gap-1.5 rounded-full bg-surface-100 px-2.5 text-[11.5px] font-semibold text-surface-700">
                    <Clock size={12} />{formatTime(detail.time)}
                  </span>
                )}
              </div>
              <div className={sectionLabel}>Description</div>
              {detail.description
                ? <p className="whitespace-pre-wrap text-[13.5px] leading-5 text-surface-700">{detail.description}</p>
                : <p className="text-[13.5px] italic text-surface-500">No description added.</p>}
              <div className={sectionLabel}>Tags</div>
              {detail.tags.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {detail.tags.map(tag => <span key={tag} className="inline-flex h-6 items-center rounded-lg bg-[var(--color-info-bg)] px-2.5 text-xs font-semibold text-[color:var(--color-info-fg)]">#{tag}</span>)}
                </div>
              ) : <p className="text-[13px] text-surface-500">No tags</p>}
            </div>
            <div className="grid shrink-0 grid-cols-3 gap-2 border-t border-surface-200 px-3.5 pb-3.5 pt-2.5">
              <Button className="min-w-0 px-2" onClick={() => props.onToggle(detail.id, !detail.done)}>{detail.done ? "Mark as open" : "Mark as done"}</Button>
              <Button className="min-w-0 px-2" variant="outline" onClick={() => setView({ kind: "form", id: detail.id })}>Edit</Button>
              <Button
                className="min-w-0 px-2"
                aria-label={confirmDelete ? "Confirm delete" : "Delete"}
                variant="destructive"
                onClick={() => {
                  if (!confirmDelete) { setConfirmDelete(true); return; }
                  setConfirmDelete(false);
                  void props.onDelete(detail.id).then(() => setView({ kind: "list" })).catch(() => {});
                }}
              >
                {confirmDelete ? "Confirm" : "Delete"}
              </Button>
            </div>
          </>
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 text-sm text-surface-500">
            This todo no longer exists.
            <Button variant="outline" size="sm" onClick={() => setView({ kind: "list" })}>Back to list</Button>
          </div>
        )
      )}

      {view.kind === "form" && (
        <TodoForm
          key={view.id ?? "new"}
          headingId={headingId}
          dateKey={dateKey}
          existing={view.id ? lookup(view.id) : undefined}
          guardRef={guardRef}
          onCancel={() => setView(view.id ? { kind: "detail", id: view.id } : { kind: "list" })}
          onClose={requestClose}
          onSave={async (draft) => {
            const saved = await props.onSave(draft, view.id);
            const target = view.id ?? saved?.id;
            setView(target ? { kind: "detail", id: target } : { kind: "list" });
          }}
        />
      )}
    </div>
  );
}

interface FormProps {
  headingId: string;
  dateKey: string;
  existing: CalendarTodo | undefined;
  guardRef: React.MutableRefObject<(() => boolean) | null>;
  onCancel: () => void;
  onClose: () => void;
  onSave: (draft: TodoDraft) => Promise<void>;
}

function TodoForm({ headingId, dateKey, existing, guardRef, onCancel, onClose, onSave }: FormProps) {
  const initial = React.useMemo<TodoDraft>(() => ({
    title: existing?.title ?? "",
    description: existing?.description ?? "",
    category: existing ? categoryOf(existing.tone) : "work",
    tags: existing?.tags ?? [],
    time: existing?.time ?? null,
    date: existing ? existing.date.split("T")[0]! : dateKey,
  }), [existing, dateKey]);

  const [draft, setDraft] = React.useState<TodoDraft>(initial);
  const [tagText, setTagText] = React.useState("");
  const [saveErr, setSaveErr] = React.useState("");
  const [confirmDiscard, setConfirmDiscard] = React.useState(false);
  const [touched, setTouched] = React.useState(false);

  const dirty = JSON.stringify(draft) !== JSON.stringify(initial) || tagText.trim() !== "";
  const today = todayKey();
  const originalDate = existing ? existing.date.split("T")[0]! : undefined;
  const errors = validateDraft(draft, today, originalDate);
  const valid = Object.keys(errors).length === 0;

  // Esc / X / Cancel all ask before throwing away typed work.
  React.useEffect(() => {
    guardRef.current = () => { if (dirty) { setConfirmDiscard(true); return true; } return false; };
    return () => { guardRef.current = null; };
  }, [dirty, guardRef]);

  const patch = (p: Partial<TodoDraft>) => setDraft(d => ({ ...d, ...p }));

  function addTags(parts: string[]) {
    patch({ tags: normalizeTags([...draft.tags, ...parts]) });
  }

  async function save() {
    setTouched(true);
    if (!valid) throw new Error("validation");
    setSaveErr("");
    const pending = splitTagInput(tagText);
    try {
      await onSave({ ...draft, title: draft.title.trim(), tags: normalizeTags([...draft.tags, ...pending]) });
    } catch (err) {
      setSaveErr(err instanceof Error && err.message !== "validation" ? "Couldn't save. Try again." : "Couldn't save. Try again.");
      throw err;
    }
  }

  function cancel() { if (dirty) setConfirmDiscard(true); else onCancel(); }

  return (
    <>
      <div className="flex shrink-0 items-start justify-between pb-2 pl-[18px] pr-3 pt-4">
        <div>
          <h2 id={headingId} className="text-[17px] font-bold tracking-tight">{existing ? "Edit todo" : "New todo"}</h2>
          <p className="mt-0.5 text-[12.5px] text-surface-500">{formatDayTitle(draft.date || dateKey)}</p>
        </div>
        <button type="button" aria-label="Close" className={iconBtn} onClick={onClose}><X size={16} /></button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto px-[18px] pb-2 pt-0.5">
        <div>
          <label htmlFor="td-title" className={fieldLabel}>Title</label>
          <Input
            id="td-title"
            autoFocus
            value={draft.title}
            maxLength={TODO_LIMITS.title}
            placeholder="e.g. Client call, flight booking…"
            aria-invalid={touched && !!errors.title}
            onChange={(e) => patch({ title: e.target.value })}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void save().catch(() => {}); } }}
          />
          {draft.title.length > 100 && <p className="mt-1 text-right text-[11px] text-surface-500">{draft.title.length}/{TODO_LIMITS.title}</p>}
          {touched && errors.title && <p className="mt-1 text-xs text-error">{errors.title}</p>}
        </div>

        <div>
          <label htmlFor="td-desc" className={fieldLabel}>Description</label>
          <Textarea
            id="td-desc"
            rows={4}
            value={draft.description}
            maxLength={TODO_LIMITS.description}
            onChange={(e) => patch({ description: e.target.value })}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); void save().catch(() => {}); } }}
          />
        </div>

        <div>
          <div className={fieldLabel}>Category</div>
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Category">
            {CATEGORY_ORDER.map(c => (
              <button
                key={c}
                type="button"
                aria-pressed={draft.category === c}
                onClick={() => patch({ category: c })}
                className={cn(
                  "inline-flex h-7 items-center gap-1.5 rounded-full px-[11px] text-xs font-semibold opacity-50 transition-opacity focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500 aria-pressed:opacity-100 aria-pressed:ring-[1.5px] aria-pressed:ring-inset aria-pressed:ring-current",
                  CATEGORY_STYLE[c].chip,
                )}
              >
                <i className={cn("size-[7px] rounded-full", CATEGORY_STYLE[c].dot)} />{CATEGORY_LABEL[c]}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label htmlFor="td-tags" className={fieldLabel}>Tags</label>
          <div className="flex flex-wrap gap-1.5">
            {draft.tags.map(tag => (
              <span key={tag} className="inline-flex h-6 items-center rounded-lg bg-[var(--color-info-bg)] pl-2.5 pr-1 text-xs font-semibold text-[color:var(--color-info-fg)]">
                #{tag}
                <button type="button" aria-label={`Remove tag ${tag}`} className="ml-1 flex size-4 items-center justify-center rounded-full hover:bg-surface-200" onClick={() => patch({ tags: draft.tags.filter(t => t !== tag) })}>
                  <X size={10} />
                </button>
              </span>
            ))}
          </div>
          <Input
            id="td-tags"
            className="mt-1.5"
            value={tagText}
            placeholder={draft.tags.length >= TODO_LIMITS.tags ? "Max 8 tags" : "Type a tag, press Enter"}
            disabled={draft.tags.length >= TODO_LIMITS.tags}
            onChange={(e) => {
              const v = e.target.value;
              if (v.includes(",")) { addTags(splitTagInput(v)); setTagText(""); } else setTagText(v);
            }}
            onPaste={(e) => {
              const text = e.clipboardData.getData("text");
              if (/[,\n]/.test(text)) { e.preventDefault(); addTags(splitTagInput(text)); }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); if (tagText.trim()) { addTags([tagText]); setTagText(""); } }
              else if (e.key === "Backspace" && !tagText && draft.tags.length) patch({ tags: draft.tags.slice(0, -1) });
            }}
          />
        </div>

        <div className="flex gap-2">
          <div className="min-w-0 flex-1">
            <label htmlFor="td-date" className={fieldLabel}>Date</label>
            <DateInput id="td-date" value={draft.date} min={originalDate && originalDate < today ? originalDate : today} onChange={(iso) => patch({ date: iso })} />
            {errors.date && <p className="mt-1 text-xs text-error">{errors.date}</p>}
          </div>
          <div className="w-[120px]">
            <label htmlFor="td-time" className={fieldLabel}>Time</label>
            <TimeInput id="td-time" value={draft.time ?? ""} onChange={(hhmm) => patch({ time: hhmm })} aria-label="Time" />
            {draft.time && (
              <button type="button" className="mt-1 text-[11px] font-semibold text-brand-600 underline" onClick={() => patch({ time: null })}>Clear time</button>
            )}
          </div>
        </div>
        {saveErr && <p role="alert" className="text-xs text-error">{saveErr}</p>}
      </div>

      <div className="shrink-0 border-t border-surface-200 px-3.5 pb-3.5 pt-2.5">
        {confirmDiscard ? (
          <div className="flex items-center justify-between gap-2" role="alert">
            <span className="text-[13px] font-semibold">Discard changes?</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setConfirmDiscard(false)}>Keep editing</Button>
              <Button variant="destructive" size="sm" onClick={onCancel}>Discard</Button>
            </div>
          </div>
        ) : (
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={cancel}>Cancel</Button>
            <AsyncButton onClick={() => save()} disabled={!valid}>Save</AsyncButton>
          </div>
        )}
      </div>
    </>
  );
}
